import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useCallback, useEffect } from 'react';

import { Loader } from 'theSource/components';
import { useApiMutation, useApiQuery } from 'theSource/utils';
import {
  ACCEPT_TERMS_OF_USE_QUERY_KEY,
  mapToAcceptTermsOfUseRequestDto,
  saveAgreementCheckToLocalStorage,
  TERMS_OF_USE_STATUS_QUERY_KEY,
} from './utils';
import { ROUTES } from 'theSource/app/routes';
import { setTermsOfUseAccepted } from './services';
import { showNavigationMenus } from '../../services';
import { termsOfUse } from 'theSource/assets';
import { TermsOfUseForm } from './components';
import {
  AcceptTermsOfUseRequestDto,
  AcceptTermsOfUseResponseDto,
  TermsOfUseAgreementDto,
} from './types';
import {
  useConfigurationQuery,
  useSubscriberQuery,
  useCompanySettingsQuery,
} from 'theSource/queries';
import { selectRedirectLink, setRedirectLink } from 'theSource/features/Login';
import { selectIsAdmin } from 'theSource/features/AccessDenied';

export const TermsOfUse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const redirectLink = useSelector(selectRedirectLink);
  const isAdmin = useSelector(selectIsAdmin);
  const { configuration } = useConfigurationQuery();
  const { subscriber } = useSubscriberQuery({ restrictAdmin: true });

  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const lumosEnabled = companySettings?.data.lumos_feature_toggle;
  const sourceEnabled = companySettings?.data.source_license;

  const { data: userAgreements, isLoading: isLoadingGet } = useApiQuery<
    TermsOfUseAgreementDto[]
  >({
    enabled: !isAdmin && sourceEnabled,
    queryKey: TERMS_OF_USE_STATUS_QUERY_KEY,
    endpoint: `${configuration?.tncApiHost}/agreements/${
      subscriber?.data.id.value as number
    }`,
  });

  const isAccepted = Boolean(
    userAgreements?.find(
      (agreement) => agreement.versionDate === termsOfUse.version
    )
  );

  const handleAccepted = useCallback(
    (redirectLink: string | null) => {
      dispatch(showNavigationMenus());
      dispatch(setTermsOfUseAccepted());
      saveAgreementCheckToLocalStorage(subscriber?.data.id.value as number);
      navigate(redirectLink || ROUTES.main.home.path);
      dispatch(setRedirectLink(null));
    },
    [dispatch, navigate, subscriber]
  );

  useEffect(() => {
    if (isAccepted) {
      handleAccepted(redirectLink);
    }
  }, [isAccepted, handleAccepted, redirectLink]);

  useEffect(() => {
    if (isAdmin && sourceEnabled) {
      navigate(ROUTES.public.accessDenied.path);
    } else if (isAdmin && !sourceEnabled && lumosEnabled) {
      handleAccepted(ROUTES.enrollment.path);
    } else if (!isAdmin && !sourceEnabled && lumosEnabled) {
      handleAccepted(ROUTES.enrollment.path);
    }
  }, [isAdmin, lumosEnabled, sourceEnabled, handleAccepted, navigate]);

  const { isLoading: isLoadingPost, mutate: acceptTermsOfUse } = useApiMutation<
    AcceptTermsOfUseResponseDto,
    unknown,
    AcceptTermsOfUseRequestDto
  >({
    queryKey: ACCEPT_TERMS_OF_USE_QUERY_KEY,
    method: 'post',
    endpoint: `${configuration?.tncApiHost}/agreements`,
    onSuccess: () => handleAccepted(null),
    showSuccessSnackbar: false,
    snackbarOptions: {
      position: 'aboveMenu',
    },
  });

  const isLoading = isLoadingGet || isLoadingPost || isAccepted;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <TermsOfUseForm
        handleSubmit={() =>
          acceptTermsOfUse(mapToAcceptTermsOfUseRequestDto(subscriber))
        }
      />
    </>
  );
};
